import { defaultTo } from 'lodash';
import { DateTime } from 'luxon';
import { HospitalPrescriptionModel } from './HospitalPrescriptionModel';
import {
  IHospitalPrescriptionRefillListingModel,
  HospitalPrescriptionRefillListingModel,
} from './HospitalPrescriptionRefillListingModel';
import { IModel, Model, ModelRecord } from './Model';
import { PhotoModel } from './PhotoModel';

export interface IHospitalPrescriptionListingModel extends IModel {
  hospitalPrescriptionId: number;
  hospitalPrescriptionStatusId: number | undefined;
  hospitalPrescriptionRequestSourceId: number | undefined;
  petId: number;
  petName: string;
  petSpecies: string;
  petBreed: string;
  petBirthDate: string;
  petWeight: number;
  visitSalesItemId: number | undefined;
  salesDate: string;
  prescribedProduct: string;
  expirationDate: string;
  lastFillDate: string | undefined;
  fillByDate: string;
  completedDate: string | undefined;
  quantity: string | undefined;
  remainingRefills: number;
  notes: string | undefined;
  instructions: string;
  unitOfMeasure: string | undefined;
  veterinarianId: number | undefined;
  veterinarianName: string | undefined;
  veterinarianFirstName: string | undefined;
  veterinarianLastName: string | undefined;
  veterinarianLicenseNumber: string | undefined;
  veterinarianLicenseStateId: number | undefined;
  veterinarianLicenseStateAbbreviation: string | undefined;
  veterinarianLicenseExpirationDate: string | undefined;
  veterinarianEmail: string | undefined;
  veterinarianSignaturePhotoId: number | undefined;
  veterinarianSignaturePhotoURL: string | undefined;
  fullName: string | undefined;
  optionDisplayName: string | undefined;
  salesItemOptionId: number | undefined;
  orderNotes: string | undefined;
  creatorFirstName: string | undefined;
  creatorLastName: string | undefined;
  creatorTitle: string | undefined;
  isHidden: boolean;
  dateHidden: string;
  hiddenByFirstName: string | undefined;
  hiddenByLastName: string | undefined;
  hiddenByTitle: string | undefined;
  dateCreated: string;
  dateUpdated: string;
  hospitalPrescriptionRefills: IHospitalPrescriptionRefillListingModel[];
  dose: string | undefined;
  frequency: string;
  clientId: number;
  clientFirstName: string;
  clientLastName: string;
  clientAddress: string;
  clientCity: string;
  clientState: string;
  clientZipcode: string;
  cancelledByFirstName: string | undefined;
  cancelledByLastName: string | undefined;
  cancelledByTitle: string | undefined;
  cancelNotes: string | undefined;
  dateCancelled: string | undefined;
  clientCanSMS: boolean;
}

export class HospitalPrescriptionListingModel
  extends Model
  implements ModelRecord<IHospitalPrescriptionListingModel>
{
  hospitalPrescriptionId: number;
  hospitalPrescriptionStatusId: number | undefined;
  hospitalPrescriptionRequestSourceId: number | undefined;
  petId: number;
  petName: string;
  petSpecies: string;
  petBreed: string;
  petBirthDate: string;
  petWeight: number;
  visitSalesItemId: number | undefined;
  salesDate: DateTime;
  prescribedProduct: string;
  expirationDate: DateTime;
  lastFillDate: DateTime | undefined;
  fillByDate: DateTime | undefined;
  completedDate: DateTime | undefined;
  quantity: string | undefined;
  remainingRefills: number;
  notes: string | undefined;
  instructions: string | undefined;
  unitOfMeasure: string | undefined;
  veterinarianId: number | undefined;
  veterinarianName: string | undefined;
  veterinarianFirstName: string | undefined;
  veterinarianLastName: string | undefined;
  veterinarianLicenseNumber: string | undefined;
  veterinarianLicenseStateId: number | undefined;
  veterinarianLicenseStateAbbreviation: string | undefined;
  veterinarianLicenseExpirationDate: DateTime | undefined;
  veterinarianEmail: string | undefined;
  veterinarianSignaturePhotoId: number | undefined;
  veterinarianSignaturePhotoURL: string | undefined;
  fullName: string | undefined;
  optionDisplayName: string | undefined;
  salesItemOptionId: number | undefined;
  orderNotes: string | undefined;
  creatorFirstName: string | undefined;
  creatorLastName: string | undefined;
  creatorTitle: string | undefined;
  isHidden: boolean;
  dateHidden: DateTime | undefined;
  hiddenByFirstName: string | undefined;
  hiddenByLastName: string | undefined;
  hiddenByTitle: string | undefined;
  dateCreated: DateTime | undefined;
  dateUpdated: DateTime | undefined;
  hospitalPrescriptionRefills: HospitalPrescriptionRefillListingModel[];
  dose: string | undefined;
  frequency: string | undefined;
  clientId: number;
  clientFirstName: string;
  clientLastName: string;
  clientAddress: string;
  clientCity: string;
  clientState: string;
  clientZipcode: string;
  cancelledByFirstName: string | undefined;
  cancelledByLastName: string | undefined;
  cancelledByTitle: string | undefined;
  cancelNotes: string | undefined;
  dateCancelled: string | undefined;
  clientCanSMS: boolean;

  constructor(params?: Partial<IHospitalPrescriptionListingModel>) {
    super(params);
    this.hospitalPrescriptionId = defaultTo(params?.hospitalPrescriptionId, 0);
    this.hospitalPrescriptionRequestSourceId = defaultTo(
      params?.hospitalPrescriptionRequestSourceId,
      0,
    );
    this.prescribedProduct = defaultTo(params?.prescribedProduct, '');
    this.remainingRefills = defaultTo(params?.remainingRefills, 0);
    this.quantity = params?.quantity;
    this.unitOfMeasure = params?.unitOfMeasure;
    this.dose = params?.dose;
    this.frequency = params?.frequency;
    this.instructions = params?.instructions;
    this.expirationDate = params?.expirationDate
      ? DateTime.fromISO(params?.expirationDate, { zone: 'utc' })
      : DateTime.utc().plus({ year: 1 });
    this.notes = params?.notes;
    this.lastFillDate = params?.lastFillDate
      ? DateTime.fromISO(params?.lastFillDate)
      : undefined;
    this.salesDate = params?.salesDate
      ? DateTime.fromISO(params?.salesDate, { zone: 'utc' })
      : DateTime.utc();
    this.veterinarianName = params?.veterinarianName;
    this.veterinarianFirstName = params?.veterinarianFirstName;
    this.veterinarianLastName = params?.veterinarianLastName;
    this.veterinarianLicenseNumber = params?.veterinarianLicenseNumber;
    this.veterinarianLicenseStateId = params?.veterinarianLicenseStateId;
    this.veterinarianLicenseStateAbbreviation =
      params?.veterinarianLicenseStateAbbreviation;
    this.veterinarianLicenseExpirationDate =
      params?.veterinarianLicenseExpirationDate
        ? DateTime.fromISO(params?.veterinarianLicenseExpirationDate)
        : undefined;
    this.veterinarianEmail = params?.veterinarianEmail;
    this.fullName = params?.fullName;
    this.optionDisplayName = params?.optionDisplayName;
    this.fillByDate = params?.fillByDate
      ? DateTime.fromISO(params?.fillByDate)
      : undefined;
    this.completedDate = params?.completedDate
      ? DateTime.fromISO(params?.completedDate)
      : undefined;
    this.petId = defaultTo(params?.petId, 0);
    this.petName = defaultTo(params?.petName, '');
    this.petSpecies = defaultTo(params?.petSpecies, '');
    this.petBreed = defaultTo(params?.petBreed, '');
    this.petBirthDate = defaultTo(params?.petBirthDate, '');
    this.petWeight = defaultTo(params?.petWeight, 0);
    this.visitSalesItemId = params?.visitSalesItemId;
    this.hospitalPrescriptionRefills = defaultTo(
      params?.hospitalPrescriptionRefills,
      [],
    ).map((a) => new HospitalPrescriptionRefillListingModel(a));
    this.veterinarianId = params?.veterinarianId;
    this.veterinarianSignaturePhotoId = params?.veterinarianSignaturePhotoId;
    this.veterinarianSignaturePhotoURL = defaultTo(
      params?.veterinarianSignaturePhotoURL,
      undefined,
    );
    this.salesItemOptionId = params?.salesItemOptionId;
    this.hospitalPrescriptionStatusId = params?.hospitalPrescriptionStatusId;
    this.orderNotes = params?.orderNotes;
    this.dateCreated = params?.dateCreated
      ? DateTime.fromISO(params?.dateCreated, { zone: 'utc' })
      : undefined;
    this.dateUpdated = params?.dateUpdated
      ? DateTime.fromISO(params?.dateUpdated, { zone: 'utc' })
      : undefined;
    this.creatorFirstName = defaultTo(params?.creatorFirstName, undefined);
    this.creatorLastName = defaultTo(params?.creatorLastName, undefined);
    this.creatorTitle = defaultTo(params?.creatorTitle, undefined);
    this.isHidden = defaultTo(params?.isHidden, false);
    this.dateHidden = params?.dateHidden
      ? DateTime.fromISO(params?.dateHidden)
      : undefined;
    this.hiddenByFirstName = defaultTo(params?.hiddenByFirstName, undefined);
    this.hiddenByLastName = defaultTo(params?.hiddenByLastName, undefined);
    this.hiddenByTitle = defaultTo(params?.hiddenByTitle, undefined);
    this.rowVersion = defaultTo(params?.rowVersion, '');
    this.clientId = defaultTo(params?.clientId, 0);
    this.clientFirstName = defaultTo(params?.clientFirstName, '');
    this.clientLastName = defaultTo(params?.clientLastName, '');
    this.clientAddress = defaultTo(params?.clientAddress, '');
    this.clientCity = defaultTo(params?.clientCity, '');
    this.clientState = defaultTo(params?.clientState, '');
    this.clientZipcode = defaultTo(params?.clientZipcode, '');
    this.cancelledByFirstName = defaultTo(params?.cancelledByFirstName, '');
    this.cancelledByLastName = defaultTo(params?.cancelledByLastName, '');
    this.cancelledByTitle = defaultTo(params?.cancelledByTitle, '');
    this.dateCancelled = defaultTo(params?.dateCancelled, '');
    this.cancelNotes = defaultTo(params?.cancelNotes, '');
    this.clientCanSMS = defaultTo(params?.clientCanSMS, false);
  }

  toHospitalPrescriptionModel(): HospitalPrescriptionModel {
    return new HospitalPrescriptionModel({
      hospitalPrescriptionId: this.hospitalPrescriptionId,
      hospitalPrescriptionRequestSourceId:
        this.hospitalPrescriptionRequestSourceId,
      prescribedProduct: this.prescribedProduct,
      remainingRefills: defaultTo(Number(this.remainingRefills), 0),
      quantity: this.quantity,
      unitOfMeasure: this.unitOfMeasure,
      dose: this.dose,
      frequency: this.frequency,
      instructions: this.instructions,
      expirationDate: defaultTo(this.expirationDate?.toISO(), undefined),
      notes: this.notes,
      salesDate: defaultTo(this.salesDate?.toISO(), undefined),
      veterinarianName: this.veterinarianName,
      veterinarianFirstName: this.veterinarianFirstName,
      veterinarianLastName: this.veterinarianLastName,
      veterinarianLicenseNumber: this.veterinarianLicenseNumber,
      veterinarianLicenseStateId: this.veterinarianLicenseStateId,
      veterinarianLicenseStateAbbreviation:
        this.veterinarianLicenseStateAbbreviation,
      veterinarianLicenseExpirationDate: defaultTo(
        this.veterinarianLicenseExpirationDate?.toISO(),
        undefined,
      ),
      veterinarianEmail: this.veterinarianEmail,
      fullName: this.fullName,
      optionDisplayName: this.optionDisplayName,
      fillByDate: defaultTo(this.fillByDate?.toISO(), undefined),
      completedDate: defaultTo(this.completedDate?.toISO(), undefined),
      petId: this.petId,
      veterinarianSignaturePhotoId: this.veterinarianSignaturePhotoId,
      veterinarianSignaturePhoto: this.veterinarianSignaturePhotoURL
        ? new PhotoModel({ photoUrl: this.veterinarianSignaturePhotoURL })
        : undefined,
      veterinarianId: this.veterinarianId,
      salesItemOptionId: this.salesItemOptionId,
      hospitalPrescriptionStatusId: this.hospitalPrescriptionStatusId,
      orderNotes: this.orderNotes,
      rowVersion: this.rowVersion,
      dateCreated: defaultTo(this.dateCreated?.toISO(), undefined),
      dateUpdated: defaultTo(this.dateUpdated?.toISO(), undefined),
    });
  }
}
